<template>
  <div class="mobile-vehicle-models" :class="{ 'list-view-active': viewMode === 'list' }">
    <!-- Store not ready -->
    <div v-if="!modelsStore" class="flex justify-center py-8">
      <div class="text-muted-foreground">Initializing...</div>
    </div>
    
    <!-- Loading State -->
    <div v-else-if="modelsStore.loading" class="flex justify-center py-8">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
    
    <!-- Error State -->
    <div v-else-if="modelsStore.error" class="py-8 text-center">
      <p class="text-destructive mb-4">{{ modelsStore.error }}</p>
      <button 
        @click="retryFetch" 
        class="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
      >
        Retry
      </button>
    </div>
    
    <!-- Content -->
    <div v-else>
      <!-- Category Selector -->
      <div class="mb-4">
      <div class="relative">
        <button
          @click="showCategoryDropdown = !showCategoryDropdown"
          class="w-full flex items-center justify-between px-4 py-3 bg-secondary rounded-lg text-foreground"
        >
          <span class="font-medium">{{ activeCategory || 'Select Category' }}</span>
          <ChevronDownIcon
            class="h-5 w-5 text-muted-foreground transition-transform duration-200"
            :class="{ 'rotate-180': showCategoryDropdown }"
          />
        </button>

        <transition
          enter-active-class="transition ease-out duration-200"
          enter-from-class="opacity-0 translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition ease-in duration-150"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-1"
        >
          <div
            v-if="showCategoryDropdown"
            ref="categoryDropdownRef"
            class="absolute z-10 mt-1 w-full bg-background border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto"
          >
            <div class="py-1">
              <button
                v-for="category in modelsStore.uniqueCategories"
                :key="category"
                @click="selectCategory(category)"
                class="w-full text-left px-4 py-2 hover:bg-accent transition-colors"
                :class="activeCategory === category ? 'text-primary font-medium' : 'text-foreground'"
              >
                {{ category }}
              </button>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Mobile Sort and View Controls -->
    <div class="flex items-center justify-between mb-4 px-1">
      <!-- Model Filter Dropdown -->
      <div ref="filterDropdownRef" class="relative">
        <button
          @click="showFilterOptions = !showFilterOptions"
          class="flex items-center space-x-2 bg-muted/50 border border-border/50 rounded-lg px-3 py-1.5 text-sm font-medium text-foreground hover:bg-muted focus:outline-hidden focus:ring-2 focus:ring-primary/30 transition-colors"
        >
          <span class="text-sm text-muted-foreground">Filter by:</span>
          <span class="font-medium">{{ selectedModelNameFilter }}</span>
          <ChevronDownIcon class="h-4 w-4 text-muted-foreground transition-transform duration-200" :class="{ 'rotate-180': showFilterOptions }"/>
        </button>

        <!-- Filter Options Popover -->
        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="transform opacity-0 scale-95"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0 scale-95"
        >
          <div
            v-if="showFilterOptions"
            class="absolute z-20 mt-1 w-60 origin-top-left bg-background border border-border rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-hidden py-1 max-h-60 overflow-y-auto"
          >
            <!-- All Models Option -->
            <button
              @click="selectModelFilter('All Models')"
              class="w-full text-left px-4 py-2 text-sm flex items-center justify-between"
              :class="selectedModelNameFilter === 'All Models' ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent'"
            >
              All Models
              <CheckIcon v-if="selectedModelNameFilter === 'All Models'" class="h-4 w-4 text-primary"/>
            </button>
            <!-- Model Title Options -->
            <button
              v-for="title in uniqueModelTitles"
              :key="title"
              @click="selectModelFilter(title)"
              class="w-full text-left px-4 py-2 text-sm flex items-center justify-between"
              :class="selectedModelNameFilter === title ? 'bg-accent text-accent-foreground' : 'text-foreground hover:bg-accent'"
            >
              {{ title }}
              <CheckIcon v-if="selectedModelNameFilter === title" class="h-4 w-4 text-primary"/>
            </button>
          </div>
        </transition>
      </div>

      <!-- Display Mode Toggle -->
      <div class="flex items-center border border-border/50 rounded-lg bg-muted/50 overflow-hidden">
        <button
          @click="viewMode = 'grid'"
          class="p-1.5 focus:outline-hidden transition-colors relative"
          :class="viewMode === 'grid' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'"
          :disabled="isTransitioning"
        >
          <Squares2X2Icon class="h-5 w-5" :class="{ 'opacity-50': isTransitioning && viewMode !== 'grid' }" />
        </button>
        <div class="w-px h-4 bg-border/50"></div>
        <button
          @click="viewMode = 'list'"
          class="p-1.5 focus:outline-hidden transition-colors relative"
          :class="viewMode === 'list' ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted'"
          :disabled="isTransitioning"
        >
          <ListBulletIcon class="h-5 w-5" :class="{ 'opacity-50': isTransitioning && viewMode !== 'list' }" />
        </button>
      </div>
    </div>

    <!-- Models Carousel -->
    <div class="relative px-1" :class="{ 'opacity-75 pointer-events-none': isTransitioning }">
      <!-- Grid View -->
      <div v-if="viewMode === 'grid'" class="animate-in fade-in-50 duration-300">
        <Carousel
          ref="carouselRef"
          :key="`carousel-${viewMode}-${activeCategory}`"
          class="w-full h-auto"
          :opts="{
            loop: false,
            align: 'center',
            dragFree: true,
            containScroll: 'trimSnaps',
            slidesToScroll: 1
          }"
          :plugins="[autoHeightPlugin, autoplayPlugin]"
          @init-api="onCarouselInit"
        >
          <CarouselContent class="h-auto">
            <CarouselItem
              v-for="model in sortedAndFilteredModels"
              :key="model.id"
              class="basis-full h-auto"
            >
              <div class="bg-background rounded-xl overflow-hidden border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300 group">
                <!-- Image Section -->
                <div class="relative w-full bg-accent/10 overflow-hidden">
                  <img
                    :src="model.model_image"
                    :alt="model.title.rendered"
                    class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-102"
                  />
                  <!-- Favorite Button (Grid View) -->
                  <button
                    @click.stop="favoritesStore.toggleFavorite(String(model.id))"
                    class="absolute top-3 right-3 z-10 p-1.5 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors focus:outline-hidden"
                    :aria-label="favoritesStore.isFavorite(String(model.id)) ? 'Remove from favorites' : 'Add to favorites'"
                  >
                    <HeartIconSolid v-if="favoritesStore.isFavorite(String(model.id))" class="h-5 w-5 text-red-500" />
                    <HeartIcon v-else class="h-5 w-5" />
                  </button>
                  <div class="absolute z-0 inset-0 bg-linear-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
                
                <!-- Content Section -->
                <div class="p-5">
                  <div class="flex flex-col h-full">
                    <div class="flex-1">
                      <div class="flex items-start justify-between gap-4">
                        <h3 class="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                          {{ model.title.rendered }}
                        </h3>
                        <button
                          v-if="model.form === 'Register'"
                          class="shrink-0 text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-full font-medium"
                        >
                          Coming Soon
                        </button>
                      </div>
                      <p v-if="model.caption" class="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {{ model.caption }}
                      </p>
                    </div>
                    <div class="flex items-center justify-between pt-4 border-t border-border/50">
                      <NuxtLink
                        :to="`/kia/${model.slug}`"
                        class="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                        @click="closeMobileMenu"
                      >
                        View Details
                        <ChevronRightIcon class="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-0.5" />
                      </NuxtLink>
                      <div class="flex items-center gap-3 text-sm text-muted-foreground">
                        <!-- Additional space for consistency with list view -->
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>
          </CarouselContent>

        </Carousel>

        <!-- Navigation Controls (only show in grid view) -->
        <div v-if="sortedAndFilteredModels.length > 1" class="flex items-center justify-center mt-4 gap-4">
          <!-- Previous Button -->
          <button
            @click="prevSlide"
            class="flex items-center justify-center w-8 h-8 rounded-full bg-background border border-border shadow-sm hover:bg-accent transition-colors"
          >
            <ChevronRightIcon class="h-4 w-4 text-foreground rotate-180" />
          </button>

          <!-- Pagination Dots -->
          <div class="flex justify-center gap-2">
            <button
              v-for="(_, index) in sortedAndFilteredModels"
              :key="index"
              @click="goToSlide(index)"
              class="w-2.5 h-2.5 rounded-full transition-colors"
              :class="currentIndex === index ? 'bg-primary' : 'bg-border hover:bg-muted-foreground'"
            ></button>
          </div>

          <!-- Next Button -->
          <button
            @click="nextSlide"
            class="flex items-center justify-center w-8 h-8 rounded-full bg-background border border-border shadow-sm hover:bg-accent transition-colors"
          >
            <ChevronRightIcon class="h-4 w-4 text-foreground" />
          </button>
        </div>
      </div>

      <!-- List View -->
      <div v-else class="space-y-4 animate-in fade-in-50 duration-300">
        <div
          v-for="model in sortedAndFilteredModels"
          :key="model.id"
          class="group bg-background rounded-xl overflow-hidden border border-border hover:border-primary/50 hover:shadow-lg transition-all duration-300"
        >
          <div class="sm:flex-row">
            <div class="relative w-full bg-accent/10 overflow-hidden">
              <img
                :src="model.model_image"
                :alt="model.title.rendered"
                class="w-full h-full relative z-1 transition-transform duration-500 group-hover:scale-102"
              />
              <div class="absolute z-0 inset-0 bg-linear-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>
            <div class="flex-1 p-5 sm:p-6">
              <div class="flex flex-col h-full">
                <div class="flex-1">
                  <div class="flex items-start justify-between gap-4">
                    <h3 class="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                      {{ model.title.rendered }}
                    </h3>
                    <button
                      v-if="model.form === 'Register'"
                      class="shrink-0 text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-full font-medium"
                    >
                      Coming Soon
                    </button>
                  </div>
                  <!-- <div v-if="model.segment" class="mb-3">
                    <span class="inline-block text-xs px-2.5 py-1 bg-secondary/50 text-secondary-foreground rounded-full font-medium">
                      {{ Array.isArray(model.segment) ? model.segment.join(', ') : model.segment }}
                    </span>
                  </div> -->
                  <p v-if="model.caption" class="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {{ model.caption }}
                  </p>
                </div>
                <div class="flex items-center justify-between pt-4 border-t border-border/50">
                  <NuxtLink
                    :to="`/kia/${model.slug}`"
                    class="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors group/link"
                    @click="closeMobileMenu"
                  >
                    View Details
                    <ChevronRightIcon class="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-0.5" />
                  </NuxtLink>
                  <div class="flex items-center gap-3 text-sm text-muted-foreground">
                    <!-- Favorite Button (List View) -->
                    <button
                      @click="favoritesStore.toggleFavorite(String(model.id))"
                      class="p-1 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors focus:outline-hidden"
                      :aria-label="favoritesStore.isFavorite(String(model.id)) ? 'Remove from favorites' : 'Add to favorites'"
                    >
                      <HeartIconSolid v-if="favoritesStore.isFavorite(String(model.id))" class="h-5 w-5 text-red-500" />
                      <HeartIcon v-else class="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="sortedAndFilteredModels.length === 0" class="py-8 text-center">
      <div class="flex flex-col items-center justify-center">
        <div class="w-16 h-16 mb-4 text-muted-foreground">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" class="w-full h-full">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M13.5 10.5l-4-4m4 4l-4 4" />
          </svg>
        </div>

        <h3 class="text-lg font-medium mb-2">No models found</h3>
        <p class="text-sm text-muted-foreground mb-4">
          No models found in this category or matching your filter.
        </p>

        <div class="flex gap-2">
          <button
            v-if="selectedModelNameFilter !== 'All Models'"
            @click="selectModelFilter('All Models')"
            class="text-sm px-3 py-1.5 bg-primary text-primary-foreground rounded-md"
          >
            Show All Models
          </button>
          <button
            v-if="activeCategory"
            @click="showCategoryDropdown = true"
            class="text-sm px-3 py-1.5 bg-muted text-foreground rounded-md"
          >
            Change Category
          </button>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, shallowRef, nextTick, onMounted } from 'vue'
import { useModelsStore } from '~/stores/models'
import { useFavoritesStore } from '~/stores/favorites'
import {
  ChevronDownIcon,
  ChevronRightIcon,
  Squares2X2Icon,
  ListBulletIcon,
  CheckIcon,
  HeartIcon
} from '@heroicons/vue/24/outline'
import { HeartIcon as HeartIconSolid } from '@heroicons/vue/24/solid'
import { onClickOutside } from '@vueuse/core'
import Carousel from '~/layers/base/components/ui/carousel/Carousel.vue'
import CarouselContent from '~/layers/base/components/ui/carousel/CarouselContent.vue'
import CarouselItem from '~/layers/base/components/ui/carousel/CarouselItem.vue'
// Removed Tooltip import - not suitable for mobile context
import AutoHeight from 'embla-carousel-auto-height'
import Autoplay from 'embla-carousel-autoplay'

const modelsStore = useModelsStore()
const favoritesStore = useFavoritesStore()
const activeCategory = ref('')
const showCategoryDropdown = ref(false)
const categoryDropdownRef = ref(null)
const currentIndex = ref(0)
const viewMode = ref<'grid' | 'list'>('list')
const showFilterOptions = ref(false)
const filterDropdownRef = ref(null)
const selectedModelNameFilter = ref('All Models')
const isTransitioning = ref(false)

// Embla Carousel refs
const carouselRef = ref<any>(null)
const carouselApi = shallowRef<any>(null)

// Embla Carousel plugins
const autoHeightPlugin = AutoHeight()
const autoplayPlugin = Autoplay({
  delay: 5000,
  stopOnInteraction: true,
  playOnInit: true
})

// Set initial active category when categories are available
watch(() => modelsStore.uniqueCategories, (categories) => {
  // Only set category if store is loaded and has data
  if (categories && categories.length > 0 && !activeCategory.value && !modelsStore.loading) {
    if (categories[0]) {
      activeCategory.value = categories[0]
    }
  }
}, { immediate: true })

// Reset current index when category changes
watch(() => activeCategory.value, () => {
  currentIndex.value = 0
})

// Reset model filter when category dropdown is toggled
watch(() => showCategoryDropdown.value, (isOpen) => {
  if (isOpen) {
    selectedModelNameFilter.value = 'All Models'
  }
})

// Handle height adjustment when switching view modes
watch(() => viewMode.value, async (newViewMode, oldViewMode) => {
  // Skip processing if component is loading or has error
  if (modelsStore.loading || modelsStore.error) {
    return
  }
  
  console.log('View mode switching from', oldViewMode, 'to', newViewMode)
  
  // Set transitioning state
  isTransitioning.value = true
  
  // Use nextTick to ensure DOM updates before triggering height recalculation
  await nextTick()
  
  // Special handling when switching TO grid view (carousel)
  if (newViewMode === 'grid' && oldViewMode === 'list') {
    console.log('Switching from list to grid - reinitializing carousel')
    
    // Wait for DOM to be fully updated and CSS transitions to settle
    await new Promise(resolve => setTimeout(resolve, 150))
    
    // Multiple attempts to reinitialize carousel for reliability
    const reinitializeCarousel = async (attempt = 1) => {
      const maxAttempts = 3
      
      try {
        if (carouselApi.value) {
          console.log(`Reinitiating carousel API (attempt ${attempt})`)
          
          // Force carousel to recalculate dimensions
          carouselApi.value.reInit()
          
          // Reset to first slide
          carouselApi.value.scrollTo(0, false)
          
          // Force height recalculation with multiple triggers
          setTimeout(() => {
            carouselApi.value?.emit('select')
            carouselApi.value?.emit('resize')
          }, 50)
          
          setTimeout(() => {
            carouselApi.value?.emit('select')
          }, 100)
          
        } else if (carouselRef.value?.carouselApi?.value) {
          console.log(`Using fallback carousel API (attempt ${attempt})`)
          const api = carouselRef.value.carouselApi.value
          api.reInit()
          api.scrollTo(0, false)
          setTimeout(() => {
            api?.emit('select')
            api?.emit('resize')
          }, 50)
        } else if (attempt < maxAttempts) {
          // Retry if carousel API isn't ready yet
          setTimeout(() => reinitializeCarousel(attempt + 1), 100)
          return
        }
      } catch (error) {
        console.warn(`Error reinitializing carousel (attempt ${attempt}):`, error)
        if (attempt < maxAttempts) {
          setTimeout(() => reinitializeCarousel(attempt + 1), 100)
        }
      }
    }
    
    await reinitializeCarousel()
  }
  
  // Trigger a gentle reflow to help parent containers adjust
  const element = document.querySelector('.mobile-vehicle-models')
  if (element instanceof HTMLElement) {
    // Force a reflow by accessing offsetHeight
    element.offsetHeight
    
    // Dispatch a custom event to notify parent containers of content change
    element.dispatchEvent(new CustomEvent('content-height-changed', {
      bubbles: true,
      detail: { viewMode: newViewMode, oldViewMode }
    }))
  }
  
  // Clear transitioning state
  setTimeout(() => {
    isTransitioning.value = false
  }, 200)
}, { flush: 'post' })

// Filter models by active category
const filteredModels = computed(() => {
  if (!activeCategory.value || modelsStore.loading || modelsStore.error) return []

  return modelsStore.modelsByCategory(activeCategory.value)
})

// ADD: Computed property for unique model titles in the current category
const uniqueModelTitles = computed((): string[] => {
  if (!filteredModels.value || filteredModels.value.length === 0) return []
  const titles: string[] = filteredModels.value.map(model => model.title.rendered);
  return [...new Set(titles)].sort((a, b) => a.localeCompare(b)); // Keep sorted A-Z for dropdown readability
})

// Filter models based on selected name filter
const sortedAndFilteredModels = computed(() => {
  if (!filteredModels.value || filteredModels.value.length === 0) return []
  
  const models = [...filteredModels.value]

  // ADD: Filtering logic
  if (selectedModelNameFilter.value !== 'All Models') {
    return models.filter(model => model.title.rendered === selectedModelNameFilter.value)
  }

  return models // Return all models if 'All Models' is selected
})

// Select a category and close dropdown
const selectCategory = (category: string) => {
  activeCategory.value = category
  showCategoryDropdown.value = false
}

// ADD: Function to select model name filter
const selectModelFilter = (modelTitle: string) => {
  selectedModelNameFilter.value = modelTitle
  showFilterOptions.value = false
  currentIndex.value = 0 // Reset slider/view when filter changes
}

// These methods are now handled by the Embla Carousel components

// Handle Embla Carousel initialization
const onCarouselInit = (api: any) => {
  console.log('Carousel initialized with API:', api)
  carouselApi.value = api

  // Store the API in a global variable for debugging
  if (typeof window !== 'undefined') {
    (window as any).__carouselApi = api
  }

  // Update currentIndex when slide changes
  api.on('select', () => {
    currentIndex.value = api.selectedScrollSnap()
  })

  // Log the current state
  console.log('Current carousel state:', {
    canScrollNext: api.canScrollNext(),
    canScrollPrev: api.canScrollPrev(),
    selectedIndex: api.selectedScrollSnap(),
    slidesInView: api.slidesInView()
  })
}

// Navigation methods for the custom buttons
const prevSlide = () => {
  console.log('Previous button clicked, API:', carouselApi.value)
  if (carouselApi.value) {
    carouselApi.value.scrollPrev()
  } else if (carouselRef.value?.carouselApi?.value) {
    // Fallback to using the ref's API
    carouselRef.value.carouselApi.value.scrollPrev()
  }
}

const nextSlide = () => {
  console.log('Next button clicked, API:', carouselApi.value)
  if (carouselApi.value) {
    carouselApi.value.scrollNext()
  } else if (carouselRef.value?.carouselApi?.value) {
    // Fallback to using the ref's API
    carouselRef.value.carouselApi.value.scrollNext()
  }
}

// Go to a specific slide
const goToSlide = (index: number) => {
  console.log('Going to slide:', index)
  if (carouselApi.value) {
    carouselApi.value.scrollTo(index)
  } else if (carouselRef.value?.carouselApi?.value) {
    // Fallback to using the ref's API
    carouselRef.value.carouselApi.value.scrollTo(index)
  }
}

// Close mobile menu when clicking "View Details"
const closeMobileMenu = () => {
  // Find the mobile menu close button and click it
  const mobileMenuCloseButton = document.querySelector('.mobile-menu-close-button')
  if (mobileMenuCloseButton instanceof HTMLElement) {
    mobileMenuCloseButton.click()
  }

  // Find all disclosure buttons with aria-expanded="true" and click them to close
  const openDisclosures = document.querySelectorAll('[aria-expanded="true"]')
  openDisclosures.forEach(button => {
    if (button instanceof HTMLElement) {
      button.click()
    }
  })
}

// Ensure models are loaded when this component is mounted
onMounted(async () => {
  try {
    if (!modelsStore.isInitialized && !modelsStore.loading) {
      await modelsStore.fetchModels()
    }
  } catch (err) {
    // Error state will be reflected via modelsStore.error
    console.error('Failed to fetch models in MobileVehicleModels:', err)
    // Force error state if needed
    if (!modelsStore.error) {
      modelsStore.error = 'Failed to load models'
    }
  }
})

// Retry fetching models
const retryFetch = async () => {
  try {
    // Reset error state before retrying
    modelsStore.error = null
    await modelsStore.fetchModels()
  } catch (err) {
    console.error('Retry failed to fetch models in MobileVehicleModels:', err)
    // Ensure error state is set
    if (!modelsStore.error) {
      modelsStore.error = 'Retry failed to load models'
    }
  }
}

// Close dropdowns when clicking outside
onClickOutside(
  categoryDropdownRef,
  () => { showCategoryDropdown.value = false }
)
onClickOutside(
  filterDropdownRef,
  () => { showFilterOptions.value = false }
)
</script>

<style scoped>
/* Height management for view mode switching */
.mobile-vehicle-models {
  transition: all 0.3s ease-in-out;
  overflow: visible;
  height: auto;
  min-height: 0;
}

.mobile-vehicle-models.list-view-active {
  /* Allow natural height expansion in list view */
  min-height: auto;
  height: auto;
  overflow: visible;
}

/* Ensure grid view (carousel) doesn't inherit list constraints */
.mobile-vehicle-models:not(.list-view-active) {
  height: auto;
  min-height: 0;
}

/* Smooth transitions for view mode changes */
.mobile-vehicle-models > div {
  transition: opacity 0.2s ease-in-out;
}

/* Grid view specific optimizations */
.mobile-vehicle-models .relative.px-1 {
  min-height: 0;
  height: auto;
}

/* Transition state styling */
.opacity-75.pointer-events-none {
  transition: opacity 0.15s ease-in-out;
}

/* Disabled button styling */
button:disabled {
  cursor: not-allowed;
}

button:disabled .opacity-50 {
  transition: opacity 0.15s ease-in-out;
}

/* Embla Carousel customization */
:deep(.embla__slide) {
  min-width: 0;
  height: auto;
  touch-action: pan-y pinch-zoom;
  contain: content; /* Improves performance by isolating the rendering */
}

/* Add momentum scrolling */
:deep(.embla__container) {
  height: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain; /* Prevent page scroll during carousel swipe */
  will-change: transform; /* Hardware acceleration hint */
}

/* Ensure carousel viewport accommodates content height */
:deep(.embla__viewport) {
  height: auto;
}

/* Optimized line clamp utilities */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  will-change: transform;
}

/* List view specific styles */
.list-view-active .space-y-4 > * + * {
  margin-top: 1rem;
}
</style>